// Observed ARCS format, not a vendor specification. Keep source files immutable.
export const BUCKETS = 624; // 6.5 inches x 96 dpi. PDF uses vector strokes.
export const MAX_ROWS = 8;
export const DAY = 86400;
export const MAX_FILE_BYTES = 128 * 1024 * 1024;

export function identifyFiles(files) {
  const selected = Array.from(files).filter(Boolean);
  if (!selected.length || selected.length > 3)
    throw new Error('Select the primary QRS and optionally A/B QRS files.');
  const names = selected.map((file) => file.name.toLowerCase());
  if (new Set(names).size !== names.length) throw new Error('Duplicate filenames selected.');
  for (const main of selected) {
    const name = main.name.toLowerCase();
    if (!name.endsWith('.qrs') || /[ab]\.qrs$/i.test(name)) continue;
    const stem = name.slice(0, -4);
    const a = selected.find((file) => file.name.toLowerCase() === `${stem}a.qrs`) || null;
    const b = selected.find((file) => file.name.toLowerCase() === `${stem}b.qrs`) || null;
    if (selected.length === 1 + Number(Boolean(a)) + Number(Boolean(b))) {
      return { main, a, b, stem: main.name.slice(0, -4) };
    }
  }
  throw new Error('A primary QRS is required; optional A/B files must share its study name.');
}

export function parseQrs(buffer, sampleRate = 128, channels = 3) {
  if (![128, 256].includes(sampleRate) || channels !== 3)
    throw new Error('Choose 128 or 256 Hz for this three-channel format.');
  if (buffer.byteLength > MAX_FILE_BYTES)
    throw new Error('QRS file exceeds the 128 MiB safety limit.');
  if (buffer.byteLength < 296 || (buffer.byteLength - 256) % 20 !== 0)
    throw new Error('Invalid QRS length: expected a 256-byte header and 20-byte records.');
  const view = new DataView(buffer);
  if (view.getUint8(2) !== 81 || view.getUint8(3) !== 82 || view.getUint8(4) !== 83)
    throw new Error('Unrecognized QRS signature.');
  const times = new Float64Array((buffer.byteLength - 256) / 20);
  const unitsPerSecond = sampleRate * channels * 2;
  for (let i = 0; i < times.length; i += 1) {
    times[i] = view.getUint32(256 + i * 20, true) / unitsPerSecond;
    if (i && times[i] <= times[i - 1]) throw new Error('QRS positions must increase strictly.');
  }
  return times;
}

function nearestIndices(x, y) {
  const result = new Uint32Array(x.length);
  let j = 0;
  for (let i = 0; i < x.length; i += 1) {
    while (j + 1 < y.length && Math.abs(y[j + 1] - x[i]) < Math.abs(y[j] - x[i])) j += 1;
    result[i] = j;
  }
  return result;
}

export function repairBeats(main, a, b, enabled = true) {
  if (!enabled || !a?.length || !b?.length) return { times: main, added: 0 };
  const ba = nearestIndices(b, a);
  const ab = nearestIndices(a, b);
  const agreed = [];
  for (let i = 0; i < b.length; i += 1) {
    if (Math.abs(b[i] - a[ba[i]]) <= 0.05 && ab[ba[i]] === i) agreed.push(b[i]);
  }
  const stable = new Uint8Array(agreed.length);
  for (let i = 0; i + 5 < agreed.length; i += 1) {
    const intervals = Array.from({ length: 5 }, (_, k) => agreed[i + k + 1] - agreed[i + k]);
    if (intervals.some((d) => d < 0.5 || d > 1.5)) continue;
    const median = [...intervals].sort((x, y) => x - y)[2];
    if (intervals.every((d) => Math.abs(d / median - 1) <= 0.25)) stable.fill(1, i, i + 6);
  }
  const end = Math.max(main[main.length - 1], a[a.length - 1], b[b.length - 1]);
  const added = [];
  let j = 0;
  for (let i = 0; i < agreed.length; i += 1) {
    if (!stable[i]) continue;
    const t = agreed[i];
    while (j < main.length && main[j] < t) j += 1;
    const left = j ? main[j - 1] : 0;
    const right = j < main.length ? main[j] : end;
    if (right - left > 2 && t - left >= 0.5 && right - t >= 0.5) added.push(t);
  }
  // Linear merge rather than sorting millions of records.
  const times = new Float64Array(main.length + added.length);
  let m = 0;
  let n = 0;
  for (let i = 0; i < times.length; i += 1) {
    if (n === added.length || (m < main.length && main[m] <= added[n])) times[i] = main[m++];
    else times[i] = added[n++];
  }
  return { times, added: added.length };
}

export function pageLayout(duration) {
  if (!Number.isFinite(duration) || duration <= 0 || duration > 366 * DAY)
    throw new Error('Recording duration is invalid or exceeds 366 days. Check the sampling rate.');
  const daysPerRow = Math.max(1, Math.ceil(duration / (MAX_ROWS * DAY)));
  const rowSeconds = daysPerRow * DAY;
  return {
    daysPerRow,
    rowSeconds,
    rows: Math.ceil(duration / rowSeconds),
    bucketSeconds: rowSeconds / BUCKETS,
  };
}

// Sample a trailing 30-second window every second of recording time.
// Each window averages instantaneous RR heart rates (60/RR), consistent with
// the graph. Only continuous valid data within one row qualifies; gaps >2 s
// break coverage. Missing time is never filled with zeros or carried forward.
export function rollingRowStats(times, rowSeconds, rows) {
  const stats = Array.from({ length: rows }, () => ({
    min: null,
    max: null,
    average: null,
    windows: 0,
  }));
  const totals = new Float64Array(rows);
  const consume = (first, last) => {
    if (last <= first || times[last] - times[first] < 30) return;
    let left = first + 1;
    let right = first + 1;
    let sum = 0;
    let count = 0;
    for (let end = Math.ceil(times[first] + 30); end <= times[last]; end += 1) {
      // A window ending exactly on a row boundary belongs to the earlier row.
      const row = Math.ceil(end / rowSeconds) - 1;
      if (row >= rows) break;
      const rowStart = row * rowSeconds;
      if (end - 30 < rowStart) {
        end = rowStart + 29;
        continue;
      }
      while (right <= last && times[right] <= end) {
        sum += 60 / (times[right] - times[right - 1]);
        count += 1;
        right += 1;
      }
      while (left < right && times[left] <= end - 30) {
        sum -= 60 / (times[left] - times[left - 1]);
        count -= 1;
        left += 1;
      }
      if (!count) continue;
      const average = sum / count;
      const entry = stats[row];
      entry.min = entry.min === null ? average : Math.min(entry.min, average);
      entry.max = entry.max === null ? average : Math.max(entry.max, average);
      entry.windows += 1;
      totals[row] += average;
    }
  };
  let first = 0;
  for (let i = 1; i < times.length; i += 1) {
    const rr = times[i] - times[i - 1];
    if (rr <= 0 || rr > 2) {
      consume(first, i - 1);
      first = i;
    }
  }
  consume(first, times.length - 1);
  return stats.map((entry, row) => ({
    ...entry,
    average: entry.windows ? totals[row] / entry.windows : null,
  }));
}

export function buildModel(main, auxiliaryA, auxiliaryB, repair = true) {
  if (!main?.length) throw new Error('Primary QRS has no beats.');
  const a = auxiliaryA ?? new Float64Array();
  const b = auxiliaryB ?? new Float64Array();
  // Matching study filenames are trusted; no cross-channel agreement/end-time gate.
  const ends = [main, a, b].filter((t) => t.length).map((t) => t[t.length - 1]);
  const duration = Math.max(...ends);
  const layout = pageLayout(duration);
  const result = repairBeats(main, a, b, repair);
  const rowStats = rollingRowStats(result.times, layout.rowSeconds, layout.rows);
  const sums = new Float64Array(layout.rows * BUCKETS);
  const counts = new Uint32Array(sums.length);
  const covered = new Float64Array(sums.length);
  let excluded = 0;
  let clippedLow = 0;
  let maxMean = 150;
  for (let i = 1; i < result.times.length; i += 1) {
    const start = result.times[i - 1];
    const end = result.times[i];
    const rr = end - start;
    if (rr > 2 || rr <= 0) {
      excluded += 1;
      continue;
    }
    // Arithmetic average of instantaneous HR (60/RR), not 60/average(RR).
    const index = Math.min(sums.length - 1, Math.floor(end / layout.bucketSeconds));
    sums[index] += 60 / rr;
    counts[index] += 1;
    const first = Math.floor(start / layout.bucketSeconds);
    const last = Math.min(sums.length - 1, Math.floor(end / layout.bucketSeconds));
    for (let k = first; k <= last; k += 1)
      covered[k] += Math.max(
        0,
        Math.min(end, (k + 1) * layout.bucketSeconds) - Math.max(start, k * layout.bucketSeconds)
      );
  }
  const buckets = Array.from(sums, (sum, i) => {
    const mean = counts[i] ? sum / counts[i] : null;
    if (mean !== null && mean < 30) clippedLow += 1;
    if (mean !== null) maxMean = Math.max(maxMean, mean);
    const span = Math.max(0, Math.min(layout.bucketSeconds, duration - i * layout.bucketSeconds));
    return { mean, count: counts[i], coverage: span ? Math.min(1, covered[i] / span) : 0 };
  });
  return {
    ...layout,
    duration,
    buckets,
    rowStats,
    added: result.added,
    original: main.length,
    excluded,
    yMax: Math.ceil(maxMean / 25) * 25,
    clippedLow,
    clippedHigh: 0,
    repair,
    sampleCounts: [main.length, a.length, b.length],
  };
}

// Parse wall-clock time without timezone or DST shifts; QRS contains elapsed time only.
export function parseStart(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) throw new Error('Enter the recording start date and time.');
  const [, year, month, day, hour, minute, second = '0'] = match;
  const stamp = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
  const check = new Date(stamp);
  if (
    check.getUTCFullYear() !== +year ||
    check.getUTCMonth() !== +month - 1 ||
    check.getUTCDate() !== +day ||
    +hour > 23 ||
    +minute > 59 ||
    +second > 59
  )
    throw new Error('Invalid recording start date/time.');
  return stamp;
}
